import cuid from "cuid";
import { Formik } from "formik";
import { defaultsDeep, get } from "lodash";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BiInfoCircle } from "react-icons/bi";
import Card from "shared/components/Card";
import Select from "shared/components/Select";
import Tooltip from "shared/components/Tooltip";
import { useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { PROCESSES } from "utils/api/graphql/queries/processes";
import { PRODUCT_RANGES } from "utils/api/graphql/queries/product-range-advisor-codes";
import { PROCESS_CONFIG_TYPES, PROCESS_TYPES } from "utils/constants";
import {
  buildProductRangeOptions,
  normalizeProcessProducts,
  serializeProcessProducts,
} from "../../productConfig";

const AdditionalConfiguration = ({
  formRef,
  dirtyRef,
  setIsDirty,
  TABS,
  data,
  onSubmit,
  config,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const FORM = t("FORM", { returnObjects: true });
  const { user: { organization: userOrganization } = {} } =
    useContext(StoreContext);

  const organizationId = get(data, "ownedBy.id") || get(userOrganization, "id");

  const { data: processesData } = useQuery(PROCESSES, {
    variables: { where: { type: PROCESS_TYPES.B2C } },
  });

  const { data: rangesData } = useQuery(PRODUCT_RANGES, {
    variables: {
      where: { ownedBy: { id: organizationId } },
      take: 500,
      orderBy: [{ code: "ASC" }],
    },
    skip: !organizationId,
  });

  const b2cProcessOptions = useMemo(
    () =>
      get(processesData, "processes.data", []).map((p) => ({
        label: p.name,
        value: p.id,
      })),
    [processesData],
  );

  const b2cProcessesMap = useMemo(() => {
    const map = {};
    get(processesData, "processes.data", []).forEach((p) => {
      map[p.id] = { id: p.id, name: p.name, theme: p.theme };
    });
    return map;
  }, [processesData]);

  const existingProductConfig = config.find(
    ({ type }) => type === PROCESS_CONFIG_TYPES.PRODUCT,
  );

  const existingAssociatedConfig = config.find(
    ({ type }) => type === PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
  );

  const initialProductsValue = normalizeProcessProducts(
    existingProductConfig?.value,
  );
  const initialAssociatedProcessIds =
    existingAssociatedConfig?.value?.processes?.map((p) => p.id) || [];
  const initialAssociatedProcessesValue = existingAssociatedConfig?.value || {
    processes: [],
  };

  return (
    <Formik
      innerRef={(formik) => {
        formRef.current = formik;
      }}
      enableReinitialize={true}
      initialValues={{
        products: initialProductsValue,
        associatedProcesses: initialAssociatedProcessIds,
        associatedProcessesValue: initialAssociatedProcessesValue,
      }}
      onSubmit={async (values) => {
        const productConfig = {
          id: existingProductConfig?.id || cuid(),
          type: PROCESS_CONFIG_TYPES.PRODUCT,
          value: serializeProcessProducts(values.products),
        };

        const associatedProcessesConfig = {
          id: existingAssociatedConfig?.id || cuid(),
          type: PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
          value: values.associatedProcessesValue || {
            processes: [],
          },
        };

        await onSubmit({
          additionalInfo: data.additionalInfo || {},
          description: data.description,
          skills: data.skills,
          config: [
            ...config.filter(
              ({ type }) =>
                type !== PROCESS_CONFIG_TYPES.PRODUCT &&
                type !== PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
            ),
            productConfig,
            associatedProcessesConfig,
          ],
          attributionType: defaultsDeep(
            config.find(
              ({ type }) => type === PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
            ),
            {
              id: cuid(),
              type: PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
              value: null,
            },
          ),
          processStatuses: data.processStatuses,
          insurancePlans: data.insurancePlans?.map(({ id }) => id),
        });
        setIsDirty((prev) => ({ ...prev, [TABS.ADDITIONAL_CONFIG]: false }));
      }}
    >
      {({ values, dirty, setFieldValue }) => {
        if (dirtyRef.current !== dirty) {
          dirtyRef.current = dirty;
          setIsDirty((prev) => ({ ...prev, [TABS.ADDITIONAL_CONFIG]: dirty }));
        }

        const productRangeOptions = buildProductRangeOptions(
          get(rangesData, "productRanges.data", []),
          values.products,
        );

        return (
          <Card
            type="simple"
            head={false}
            title={FORM.ADDITIONAL_CONFIG || "Configuration Additionnel"}
          >
            <div className="grid--2 gap-16">
              <div className="d-flex flex--column gap-8 align--stretch">
                <Select
                  mode="multiple"
                  label={
                    FORM.PRODUCT_RANGES || FORM.PRODUCT_RANGE || FORM.PRODUCT
                  }
                  labelSuffix={
                    <Tooltip title={get(FORM, "PRODUCT_RANGE_HELP", "")}>
                      <BiInfoCircle size={14} className="c-gray" />
                    </Tooltip>
                  }
                  placeholder={
                    FORM.PRODUCT_RANGES_PLACEHOLDER ||
                    FORM.PRODUCT_RANGE_PLACEHOLDER
                  }
                  options={productRangeOptions}
                  value={values.products}
                  onChange={(next) =>
                    setFieldValue("products", next == null ? [] : next)
                  }
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  disabled={!organizationId}
                />
              </div>
              <Select
                mode="multiple"
                label={FORM.ASSOCIATED_PROCESSES || "Parcours associés"}
                placeholder={
                  FORM.ASSOCIATED_PROCESSES_PLACEHOLDER ||
                  "Sélectionner les parcours B2C"
                }
                options={b2cProcessOptions}
                value={values.associatedProcesses}
                onChange={(selectedIds) => {
                  setFieldValue("associatedProcesses", selectedIds);
                  setFieldValue("associatedProcessesValue", {
                    processes: selectedIds.map(
                      (id) => b2cProcessesMap[id] || { id },
                    ),
                  });
                }}
                optionFilterProp="label"
                showSearch
              />
            </div>
          </Card>
        );
      }}
    </Formik>
  );
};

export default AdditionalConfiguration;
